import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Pencil, Trash2, Search, Calendar, Copy, RefreshCw, ChevronDown, MapPin, ArrowRight, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export const AdminCourseSessionsPage = () => {
  const { language } = useLanguage();
  const [sessions, setSessions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [deliveryModeFilter, setDeliveryModeFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  
  // Selection for bulk operations
  const [selectedSessions, setSelectedSessions] = useState([]);
  
  // Dialogs
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  
  const [editingSession, setEditingSession] = useState(null);
  const [duplicatingSession, setDuplicatingSession] = useState(null);
  
  const [formData, setFormData] = useState({
    template_id: '',
    city_id: '',
    location: '',
    location_ar: '',
    delivery_mode: 'onsite',
    start_date: '',
    end_date: '',
    price: '',
    is_featured: false,
    is_public: true,
    status: 'scheduled',
  });
  
  const [duplicateData, setDuplicateData] = useState({
    new_city_id: '',
    new_location: '',
    new_location_ar: '',
    new_start_date: '',
    new_end_date: '',
    new_delivery_mode: '',
    new_price: '',
  });
  
  const [bulkData, setBulkData] = useState({
    action: 'duplicate', // duplicate, shift, delete
    year_shift: 1,
    preserve_weekday: true,
    days: 0,
    weeks: 0,
    months: 0,
    years: 1,
  });
  
  const [generateData, setGenerateData] = useState({
    source_year: new Date().getFullYear(),
    target_year: new Date().getFullYear() + 1,
    template_ids: [],
    preserve_weekday: true,
  });

  useEffect(() => {
    fetchSessions();
    fetchTemplates();
    fetchCities();
    fetchCategories();
  }, [templateFilter, cityFilter, yearFilter, statusFilter]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/course-sessions?`;
      if (templateFilter && templateFilter !== 'all') url += `&template_id=${templateFilter}`;
      if (cityFilter && cityFilter !== 'all') url += `&city_id=${cityFilter}`;
      if (yearFilter && yearFilter !== 'all') url += `&year=${yearFilter}`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/admin/course-templates`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await fetch(`${API}/admin/training-cities`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCities(data);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/categories`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // Build query params from current filters
      const params = new URLSearchParams();
      if (yearFilter && yearFilter !== 'all') params.append('year', yearFilter);
      if (categoryFilter && categoryFilter !== 'all') params.append('category_id', categoryFilter);
      if (cityFilter && cityFilter !== 'all') params.append('city_id', cityFilter);
      if (deliveryModeFilter && deliveryModeFilter !== 'all') params.append('delivery_mode', deliveryModeFilter);
      if (languageFilter && languageFilter !== 'all') params.append('language_filter', languageFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await fetch(`${API}/admin/course-sessions/export-excel?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const yearPart = yearFilter && yearFilter !== 'all' ? `_${yearFilter}` : '';
        a.download = `WeDo_Training_Calendar${yearPart}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Training calendar exported successfully');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to export training calendar');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export training calendar');
    } finally {
      setExporting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCitySelect = (cityId) => {
    handleChange('city_id', cityId);
    if (cityId) {
      const city = cities.find(c => c.city_id === cityId);
      if (city) {
        handleChange('location', city.name);
        handleChange('location_ar', city.name_ar);
      }
    }
  };

  const openSessionDialog = (session = null) => {
    if (session) {
      setEditingSession(session);
      setFormData({
        template_id: session.template_id || '',
        city_id: session.city_id || '',
        location: session.location || '',
        location_ar: session.location_ar || '',
        delivery_mode: session.delivery_mode || 'onsite',
        start_date: session.start_date || '',
        end_date: session.end_date || '',
        price: session.price?.toString() || '',
        is_featured: session.is_featured || false,
        is_public: session.is_public !== false,
        status: session.status || 'scheduled',
      });
    } else {
      setEditingSession(null);
      setFormData({
        template_id: '',
        city_id: '',
        location: '',
        location_ar: '',
        delivery_mode: 'onsite',
        start_date: '',
        end_date: '',
        price: '',
        is_featured: false,
        is_public: true,
        status: 'scheduled',
      });
    }
    setSessionDialogOpen(true);
  };

  const openDuplicateDialog = (session) => {
    setDuplicatingSession(session);
    setDuplicateData({
      new_city_id: '',
      new_location: '',
      new_location_ar: '',
      new_start_date: '',
      new_end_date: '',
      new_delivery_mode: '',
      new_price: '',
    });
    setDuplicateDialogOpen(true);
  };

  const handleSubmitSession = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      price: formData.price ? parseFloat(formData.price) : null,
    };

    try {
      const url = editingSession 
        ? `${API}/admin/course-sessions/${editingSession.session_id}`
        : `${API}/admin/course-sessions`;
      
      const response = await fetch(url, {
        method: editingSession ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingSession ? 'Session updated' : 'Session created');
        setSessionDialogOpen(false);
        fetchSessions();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save session');
      }
    } catch (error) {
      toast.error('Failed to save session');
    }
  };

  const handleDuplicate = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API}/admin/course-sessions/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: duplicatingSession.session_id,
          ...duplicateData,
          new_price: duplicateData.new_price ? parseFloat(duplicateData.new_price) : null,
        }),
      });

      if (response.ok) {
        toast.success('Session duplicated successfully');
        setDuplicateDialogOpen(false);
        fetchSessions();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to duplicate session');
      }
    } catch (error) {
      toast.error('Failed to duplicate session');
    }
  };

  const handleBulkAction = async () => {
    if (selectedSessions.length === 0) {
      toast.error('Select sessions first');
      return;
    }

    try {
      let url, payload;
      
      if (bulkData.action === 'duplicate') {
        url = `${API}/admin/course-sessions/bulk-duplicate`;
        payload = {
          session_ids: selectedSessions,
          year_shift: bulkData.year_shift,
          preserve_weekday: bulkData.preserve_weekday,
        };
      } else if (bulkData.action === 'shift') {
        url = `${API}/admin/course-sessions/bulk-date-shift`;
        payload = {
          session_ids: selectedSessions,
          days: bulkData.days,
          weeks: bulkData.weeks,
          months: bulkData.months,
          years: bulkData.years,
        };
      } else if (bulkData.action === 'delete') {
        if (!window.confirm(`Delete ${selectedSessions.length} sessions? This cannot be undone.`)) return;
        url = `${API}/admin/course-sessions/bulk-delete`;
        payload = selectedSessions;
      }
      
      const response = await fetch(url, {
        method: bulkData.action === 'delete' ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setBulkDialogOpen(false);
        setSelectedSessions([]);
        fetchSessions();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Bulk operation failed');
      }
    } catch (error) {
      toast.error('Bulk operation failed');
    }
  };

  const handleGenerateYear = async () => {
    try {
      const response = await fetch(`${API}/admin/course-sessions/generate-year`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(generateData),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setGenerateDialogOpen(false);
        fetchSessions();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to generate schedule');
      }
    } catch (error) {
      toast.error('Failed to generate schedule');
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Delete this session?')) return;

    try {
      const response = await fetch(`${API}/admin/course-sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Session deleted');
        fetchSessions();
      } else {
        toast.error('Failed to delete session');
      }
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const toggleSelectAll = () => {
    if (selectedSessions.length === filteredSessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(filteredSessions.map(s => s.session_id));
    }
  };

  const toggleSelect = (sessionId) => {
    if (selectedSessions.includes(sessionId)) {
      setSelectedSessions(prev => prev.filter(id => id !== sessionId));
    } else {
      setSelectedSessions(prev => [...prev, sessionId]);
    }
  };

  const filteredSessions = sessions.filter(session => {
    const title = session.template_title?.toLowerCase() || '';
    const location = session.location?.toLowerCase() || '';
    return title.includes(search.toLowerCase()) || location.includes(search.toLowerCase());
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get available years from sessions
  const availableYears = [...new Set(sessions.map(s => s.start_date?.slice(0, 4)).filter(Boolean))].sort();

  return (
    <div className="p-6" data-testid="admin-course-sessions-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Course Sessions</h1>
          <p className="text-[#6B7280]">Scheduled offerings (city + dates)</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleExportExcel}
            disabled={exporting}
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4 me-2" />
            {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
          <Button variant="outline" onClick={() => setGenerateDialogOpen(true)}>
            <Calendar className="h-4 w-4 me-2" />
            Generate Year
          </Button>
          <Button className="btn-primary" onClick={() => openSessionDialog()}>
            <Plus className="h-4 w-4 me-2" />
            New Session
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
                <Input
                  placeholder="Search sessions..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="ps-9"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48"><SelectValue placeholder="All Categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.filter(c => c.category_id).map(c => (
                  <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={templateFilter} onValueChange={setTemplateFilter}>
              <SelectTrigger className="w-48"><SelectValue placeholder="All Templates" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Templates</SelectItem>
                {templates.filter(t => t.template_id).map(t => (
                  <SelectItem key={t.template_id} value={t.template_id}>{t.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Cities" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.filter(c => c.city_id).map(c => (
                  <SelectItem key={c.city_id} value={c.city_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-32"><SelectValue placeholder="All Years" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {availableYears.map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={deliveryModeFilter} onValueChange={setDeliveryModeFilter}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All Modes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modes</SelectItem>
                <SelectItem value="onsite">On-site</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
            <Select value={languageFilter} onValueChange={setLanguageFilter}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All Languages" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Languages</SelectItem>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="arabic">Arabic</SelectItem>
                <SelectItem value="bilingual">Bilingual</SelectItem>
                <SelectItem value="german">German</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selectedSessions.length > 0 && (
        <div className="bg-[#33AADD]/10 border border-[#33AADD]/30 rounded-lg p-4 mb-4 flex items-center justify-between">
          <span className="text-[#111827] font-medium">{selectedSessions.length} sessions selected</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setBulkDialogOpen(true)}>
              <RefreshCw className="h-4 w-4 me-2" />
              Bulk Actions
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedSessions([])}>
              Clear
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD] mx-auto"></div>
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-[#E5E7EB] mx-auto mb-3" />
              <p className="text-[#6B7280]">No sessions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectedSessions.length === filteredSessions.length && filteredSessions.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-end">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSessions.map((session, index) => (
                    <TableRow key={session.session_id} data-testid={`session-row-${index}`}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedSessions.includes(session.session_id)}
                          onCheckedChange={() => toggleSelect(session.session_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-[#111827]">
                          {language === 'ar' ? session.template_title_ar : session.template_title}
                        </p>
                        {session.is_featured && <Badge className="bg-[#33AADD] text-xs mt-1">Featured</Badge>}
                      </TableCell>
                      <TableCell>
                        {session.location ? (
                          <div className="flex items-center gap-1 text-[#6B7280]">
                            <MapPin className="h-3 w-3" />
                            {language === 'ar' ? session.location_ar || session.location : session.location}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {session.start_date ? (
                          <div className="text-sm">
                            <div>{formatDate(session.start_date)}</div>
                            {session.end_date && <div className="text-[#6B7280]">to {formatDate(session.end_date)}</div>}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{session.delivery_mode}</Badge>
                      </TableCell>
                      <TableCell>
                        {session.price ? `EUR ${session.price}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(session.status)}>{session.status}</Badge>
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="sm" onClick={() => openDuplicateDialog(session)} title="Duplicate">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openSessionDialog(session)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(session.session_id)} title="Delete">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Session Dialog */}
      <Dialog open={sessionDialogOpen} onOpenChange={setSessionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSession ? 'Edit' : 'New'} Course Session</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitSession} className="space-y-4">
            <div className="space-y-2">
              <Label>Course Template *</Label>
              <Select value={formData.template_id} onValueChange={(v) => handleChange('template_id', v)} required>
                <SelectTrigger><SelectValue placeholder="Select template" /></SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.template_id).map(t => (
                    <SelectItem key={t.template_id} value={t.template_id}>{t.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Select value={formData.city_id || "_custom"} onValueChange={(v) => handleCitySelect(v === "_custom" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_custom">-- Custom --</SelectItem>
                    {cities.filter(c => c.city_id).map(c => (
                      <SelectItem key={c.city_id} value={c.city_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Delivery Mode</Label>
                <Select value={formData.delivery_mode} onValueChange={(v) => handleChange('delivery_mode', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onsite">On-site</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Location (EN)</Label>
                <Input value={formData.location} onChange={(e) => handleChange('location', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Location (AR)</Label>
                <Input value={formData.location_ar} onChange={(e) => handleChange('location_ar', e.target.value)} dir="rtl" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={formData.start_date} onChange={(e) => handleChange('start_date', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={formData.end_date} onChange={(e) => handleChange('end_date', e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Price (override)</Label>
                <Input type="number" step="0.01" value={formData.price} onChange={(e) => handleChange('price', e.target.value)} placeholder="Uses template base price if empty" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Checkbox checked={formData.is_featured} onCheckedChange={(c) => handleChange('is_featured', c)} />
                <Label>Featured</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={formData.is_public} onCheckedChange={(c) => handleChange('is_public', c)} />
                <Label>Public</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSessionDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Duplicate Session</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDuplicate} className="space-y-4">
            <div className="p-3 bg-[#F9FAFB] rounded-lg">
              <p className="text-sm text-[#6B7280]">Original</p>
              <p className="font-medium">{duplicatingSession?.template_title} - {duplicatingSession?.location}</p>
              <p className="text-sm text-[#6B7280]">{formatDate(duplicatingSession?.start_date)} - {formatDate(duplicatingSession?.end_date)}</p>
            </div>

            <div className="space-y-2">
              <Label>New City</Label>
              <Select value={duplicateData.new_city_id || "_keep"} onValueChange={(v) => {
                const actualValue = v === "_keep" ? "" : v;
                setDuplicateData(prev => ({ ...prev, new_city_id: actualValue }));
                const city = cities.find(c => c.city_id === actualValue);
                if (city) {
                  setDuplicateData(prev => ({ ...prev, new_location: city.name, new_location_ar: city.name_ar }));
                }
              }}>
                <SelectTrigger><SelectValue placeholder="Keep original" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_keep">Keep original</SelectItem>
                  {cities.filter(c => c.city_id).map(c => (
                    <SelectItem key={c.city_id} value={c.city_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>New Start Date</Label>
                <Input type="date" value={duplicateData.new_start_date} onChange={(e) => setDuplicateData(prev => ({ ...prev, new_start_date: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>New End Date</Label>
                <Input type="date" value={duplicateData.new_end_date} onChange={(e) => setDuplicateData(prev => ({ ...prev, new_end_date: e.target.value }))} />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDuplicateDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary">
                <Copy className="h-4 w-4 me-2" />
                Duplicate
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Actions Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Actions ({selectedSessions.length} sessions)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={bulkData.action} onValueChange={(v) => setBulkData(prev => ({ ...prev, action: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="duplicate">Duplicate to Next Year</SelectItem>
                  <SelectItem value="shift">Shift Dates</SelectItem>
                  <SelectItem value="delete">Delete All</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {bulkData.action === 'duplicate' && (
              <>
                <div className="space-y-2">
                  <Label>Years to Shift</Label>
                  <Input type="number" min="1" value={bulkData.year_shift} onChange={(e) => setBulkData(prev => ({ ...prev, year_shift: parseInt(e.target.value) || 1 }))} />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={bulkData.preserve_weekday} onCheckedChange={(c) => setBulkData(prev => ({ ...prev, preserve_weekday: c }))} />
                  <Label>Preserve weekday (Mon-Fri pattern)</Label>
                </div>
              </>
            )}

            {bulkData.action === 'shift' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Days</Label>
                  <Input type="number" value={bulkData.days} onChange={(e) => setBulkData(prev => ({ ...prev, days: parseInt(e.target.value) || 0 }))} />
                </div>
                <div className="space-y-2">
                  <Label>Weeks</Label>
                  <Input type="number" value={bulkData.weeks} onChange={(e) => setBulkData(prev => ({ ...prev, weeks: parseInt(e.target.value) || 0 }))} />
                </div>
                <div className="space-y-2">
                  <Label>Months</Label>
                  <Input type="number" value={bulkData.months} onChange={(e) => setBulkData(prev => ({ ...prev, months: parseInt(e.target.value) || 0 }))} />
                </div>
                <div className="space-y-2">
                  <Label>Years</Label>
                  <Input type="number" value={bulkData.years} onChange={(e) => setBulkData(prev => ({ ...prev, years: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>
            )}

            {bulkData.action === 'delete' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium">Warning: This will permanently delete {selectedSessions.length} sessions.</p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleBulkAction} 
                className={bulkData.action === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'btn-primary'}
              >
                {bulkData.action === 'duplicate' && 'Duplicate Sessions'}
                {bulkData.action === 'shift' && 'Shift Dates'}
                {bulkData.action === 'delete' && 'Delete Sessions'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate Year Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate Annual Schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#6B7280]">Copy last year's schedule pattern to the next year with adjusted dates.</p>
            
            <div className="flex items-center gap-4">
              <div className="flex-1 space-y-2">
                <Label>Source Year</Label>
                <Input type="number" value={generateData.source_year} onChange={(e) => setGenerateData(prev => ({ ...prev, source_year: parseInt(e.target.value) }))} />
              </div>
              <ArrowRight className="h-6 w-6 text-[#6B7280] mt-6" />
              <div className="flex-1 space-y-2">
                <Label>Target Year</Label>
                <Input type="number" value={generateData.target_year} onChange={(e) => setGenerateData(prev => ({ ...prev, target_year: parseInt(e.target.value) }))} />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox checked={generateData.preserve_weekday} onCheckedChange={(c) => setGenerateData(prev => ({ ...prev, preserve_weekday: c }))} />
              <Label>Preserve Mon-Fri pattern</Label>
            </div>

            <div className="p-3 bg-[#F9FAFB] rounded-lg text-sm text-[#6B7280]">
              This will find all sessions from {generateData.source_year} and create copies for {generateData.target_year} with adjusted dates. Duplicate sessions will be skipped.
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleGenerateYear} className="btn-primary">
                <Calendar className="h-4 w-4 me-2" />
                Generate Schedule
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCourseSessionsPage;
